/**
 * Check if a value is a plain object.
 * @param {*} value The value to check.
 * @returns {boolean}
 */
function isObject(value) {
  return value !== null && typeof value === 'object' && value.constructor === Object;
}

/**
 * Set a param in a URLSearchParam instance.
 * @param {URLSearchParams} params The params to update.
 * @param {string} name The name of the param to update.
 * @param {string|number|boolean|Array|object} value The value for this param.
 * @returns {URLSearchParams} The updated URLSearchParams instance.
 */
function updateUrlSearchParam(params, name, value) {
  if (!value) {
    if (params.has(name)) {
      params.delete(name);
    }
    return params;
  }

  if (Array.isArray(value)) {
    value.forEach((val, index) => {
      const arrayName = `${name}[${index}]`;
      updateUrlSearchParam(params, arrayName, val);
    });
    return params;
  }

  if (isObject(value)) {
    Object.entries(value).forEach(([key, val]) => {
      const objectName = `${name}[${key}]`;
      updateUrlSearchParam(params, objectName, val);
    });
    return params;
  }

  params.set(name, value);
  return params;
}

/**
 * Transform an object to an URLSearchParams instance.
 * @param {object} obj The object to convert.
 * @param {string} defaultSearch A string of defaults search params.
 * @returns {URLSearchParams}
 */
function objectToURLSearchParams(obj, defaultSearch = window.location.search) {
  return Object.entries(obj).reduce(
    (urlSearchParams, [name, value]) => updateUrlSearchParam(urlSearchParams, name, value),
    new URLSearchParams(defaultSearch),
  );
}

/**
 * Update the history with a new state.
 * @param {string} mode Wether to push or replace the new state.
 * @param {object} options
 * @param {string} options.path The new pathname.
 * @param {URLSearchParams|object} options.search The new search params.
 * @param {string} options.hash The new hash.
 * @param {object} data The data for the new state.
 * @param {string} title The title for the new state.
 */
function updateHistory(
  mode,
  { path = window.location.pathname, search = {}, hash = window.location.hash },
  data = {},
  title = '',
) {
  if (!window.history) {
    return;
  }

  let url = path;

  const mergedSearch =
    search.constructor.name === 'URLSearchParams' ? search : objectToURLSearchParams(search);

  if (mergedSearch.toString()) {
    url += `?${mergedSearch.toString()}`;
  }

  if (hash) {
    if (hash.startsWith('#')) {
      url += hash;
    } else {
      url += `#${hash}`;
    }
  }

  const method = `${mode}State`;
  window.history[method](data, title, url);
}

/**
 * Push a new state.
 * @param {object|string} options The new state.
 * @param {object} [data] The data for the new state.
 * @param {string} [title] The title for the new state.
 */
export function push(options, data = {}, title = '') {
  updateHistory('push', options, data, title);
}

/**
 * Replace a new state.
 * @param {object|string} options The new state.
 * @param {object} [data] The data for the new state.
 * @param {string} [title] The title for the new state.
 */
export function replace(options, data = {}, title = '') {
  updateHistory('replace', options, data, title);
}
